import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { InvoiceGenerator } from '@repo/pdf';
import { WholesaleRequestListGenerator } from '@repo/pdf';
import { FormEvent, PropsWithChildren, createContext, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useLazyGetOrderByIdQuery } from '@/features/slice/order';
import { useGetStoreSettingsQuery } from '@/features/slice/settings';
import {
    WholesaleAdminRequestResponse,
    useConfirmWholesaleAdminRequestMutation,
    useGetWholesaleAdminRequestByIdQuery,
    useRejectWholesaleAdminRequestMutation,
} from '@/features/slice/wholesale-admin';
import { logError } from '@/utils';
import { mapOrderToInvoice } from '@/utils/mappers';

import { Route } from '../../../constants';
import { ConfirmOrderForm } from './schema';

type ContextProps = {
    isLoading: boolean;
    isRejecting: boolean;
    isConfirming: boolean;
    error: FetchBaseQueryError | SerializedError | undefined;
    data: WholesaleAdminRequestResponse | undefined;
    handleRejectWholesaleRequest: () => Promise<void>;
    handleSubmit: (
        values: ConfirmOrderForm | FormEvent<HTMLFormElement>,
    ) => Promise<void>;
    handleConfirmAndPrint: (values: ConfirmOrderForm) => Promise<void>;

    handleExportToPdf: () => void;
};

const WholesaleOrderRequestDetailsContext = createContext<ContextProps | null>(
    null,
);

export const WholesaleOrderRequestDetailsProvider = ({
    children,
}: PropsWithChildren) => {
    const params = useParams();
    const id = params.id as string;

    const navigate = useNavigate();

    const { data, isLoading, error } = useGetWholesaleAdminRequestByIdQuery(id);

    const [rejectWholesaleRequest, { isLoading: isRejecting }] =
        useRejectWholesaleAdminRequestMutation();
    const [confirmWholesaleRequest, { isLoading: isConfirming }] =
        useConfirmWholesaleAdminRequestMutation();
    const [getOrderById] = useLazyGetOrderByIdQuery();
    const { data: storeSettings } = useGetStoreSettingsQuery('');

    const handleRejectWholesaleRequest = async () => {
        try {
            await rejectWholesaleRequest({ id });

            toast.success('Order cancelled successfully!');
            navigate(Route.WHOLESALE_ORDER_REQUESTS);
        } catch (error) {
            logError('handleRejectWholesaleRequest', error);
            toast.error('Something went wrong!', {
                description: 'Please try again later',
            });
        }
    };

    const confirmOrder = async (
        values: ConfirmOrderForm,
        printInvoice: boolean,
    ) => {
        const payload = {
            ...(values as ConfirmOrderForm),
            items:
                (values as ConfirmOrderForm)?.items.map((item) => ({
                    product_variation_id: item.product_variation_id,
                    quantity: item.quantity,
                    wholesale_price: item.wholesale_price,
                    retail_price: item.retail_price,
                    product_name: item.product_name,
                })) || [],
        };

        try {
            const result = (await confirmWholesaleRequest({
                id,
                body: payload,
            }).unwrap()) as {
                order_id?: string;
                orderId?: string;
                data?: { order_id?: string; orderId?: string };
            };
            const orderId =
                result.order_id ??
                result.orderId ??
                result.data?.order_id ??
                result.data?.orderId;

            if (!orderId) {
                throw new Error('The confirmed order ID was not returned.');
            }

            if (printInvoice) {
                const order = await getOrderById(orderId).unwrap();
                if (!storeSettings?.data) {
                    throw new Error(
                        'Store settings are not available for printing.',
                    );
                }

                new InvoiceGenerator(
                    mapOrderToInvoice(order.data, storeSettings.data as any),
                    { type: 'sale' },
                ).print();
            }

            toast.success('Order confirmed successfully!', {
                action: {
                    label: 'View Order',
                    onClick: () =>
                        navigate(
                            Route.WHOLESALE_ORDER_DETAILS.replace(
                                ':id',
                                orderId,
                            ),
                        ),
                },
            });
            navigate(Route.WHOLESALE_ORDER_REQUESTS);
        } catch (error) {
            logError('handleConfirmWholesaleRequest', error);
            toast.error('Something went wrong!', {
                description: 'Please try again later',
            });
        }
    };

    const handleSubmit = async (
        values: ConfirmOrderForm | FormEvent<HTMLFormElement>,
    ) => confirmOrder(values as ConfirmOrderForm, false);

    const handleConfirmAndPrint = async (values: ConfirmOrderForm) =>
        confirmOrder(values, true);

    const handleExportToPdf = () => {
        const pdfGenerator = new WholesaleRequestListGenerator({
            customerName: data?.data?.customer?.name || 'N/A',
            customerPhoneNumber: data?.data?.customer?.phone_number || '',
            date: data?.data?.created_at || '',
            items:
                data?.data?.items?.map((item) => ({
                    productName: item.product_name ?? 'N/A',
                    quantity: item.quantity ?? 0,
                    retailPrice: item.retail_price ?? 0,
                    brandName: item.brand ?? 'N/A',
                    purchasePrice: item.purchase_price ?? 0,
                    wholesalePrice: item.wholesale_price ?? 0,
                    stockQuantity: item.available_quantity ?? 0,
                })) || [],
        });

        pdfGenerator.print();
    };

    return (
        <WholesaleOrderRequestDetailsContext.Provider
            value={{
                isLoading,
                error,
                data,
                handleRejectWholesaleRequest,
                handleSubmit,
                handleConfirmAndPrint,
                isRejecting,
                isConfirming,
                handleExportToPdf,
            }}
        >
            {children}
        </WholesaleOrderRequestDetailsContext.Provider>
    );
};

export function useWholesaleOrderRequestDetailsContext() {
    const context = useContext(WholesaleOrderRequestDetailsContext);
    if (!context) {
        throw new Error(
            'useWholesaleOrderRequestDetailsContext must be used within a WholesaleOrderRequestDetailsProvider',
        );
    }
    return context;
}
